import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, Button, TextField, Alert, CircularProgress } from '@mui/material';

const AddressValidationModal = ({
  open,
  onClose,
  address,
  validationDetails,
  onSubmit,
  isValidating = false
}) => {
  const [streetNumber, setStreetNumber] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Reset fields when modal opens with new address
  useEffect(() => {
    if (open && address) {
      // Try to extract street number from address1 if it exists
      const addressParts = address?.address1?.split(' ') || [];
      const possibleStreetNumber = addressParts[0]?.match(/^\d+$/) ? addressParts[0] : '';

      setStreetNumber(possibleStreetNumber || '');
      setZipCode(address?.zip || '');
      setHasAttemptedSubmit(false);
    }
  }, [open, address]);

  // Determine what fields are missing based on validation details
  const needsStreetNumber = (validationDetails?.errors || []).some(
    error => error.code === 'E.HOUSE_NUMBER.MISSING' || error.code === 'E.ADDRESS.NOT_FOUND'
  );

  const needsZipCode = (validationDetails?.errors || []).some(
    error => error.code === 'E.ZIP.NOT_FOUND' || error.code === 'E.ZIP.INVALID'
  ) || !address?.zip?.length;

  // If no specific field is detected, check if there are any validation errors
  const hasValidationErrors = (validationDetails?.errors || []).length > 0;

  // Show fields based on what is needed
  const showStreetNumberField = needsStreetNumber || (hasValidationErrors && !needsZipCode);
  const showZipCodeField = needsZipCode;

  // Get specific error messages to display to the user
  const getErrorMessages = () => {
    if (!(validationDetails?.errors || []).length) return [];

    return validationDetails.errors.map(error => error.message).filter(Boolean);
  };

  const errorMessages = getErrorMessages();

  const handleSubmit = () => {
    setHasAttemptedSubmit(true);

    // Create updated address object
    const updatedAddress = {
      ...address,
    };

    if (showStreetNumberField && streetNumber) {
      // If the address already has a street name, prepend the number
      if (address?.address1 && !address.address1.match(/^\d+/)) {
        updatedAddress.address1 = `${streetNumber} ${address.address1}`;
      }
    }

    if (showZipCodeField && zipCode) {
      updatedAddress.zip = zipCode;
    }

    onSubmit(updatedAddress);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="address-validation-modal-title"
      sx={{
        zIndex: 10300, // Standard Material UI modal z-index
      }}
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: 450 },
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 1,
        fontFamily: '"Montserrat", sans-serif', // Match app font
        maxHeight: '90vh',
        overflow: 'auto',
      }}>
        <Typography
          id="address-validation-modal-title"
          variant="h5"
          component="h2"
          gutterBottom
          sx={{
            fontWeight: 600,
            fontFamily: 'inherit',
            mb: 2
          }}
        >
          Complete Your Address
        </Typography>

        <Typography
          variant="body1"
          sx={{
            mb: 2,
            fontFamily: 'inherit',
            fontSize: '1rem',
            lineHeight: 1.5
          }}
        >
          We need a few more details to ensure your delivery arrives correctly. Please provide the missing information below.
        </Typography>

        {validationDetails?.normalizedAddress && (
          <Alert
            severity="info"
            sx={{ mb: 3, fontFamily: 'inherit' }}
          >
            We found a more accurate address from Google. Please review and confirm:
            <div style={{ marginTop: '8px' }}>
              <strong>{validationDetails?.raw?.formattedAddress || ''}</strong>
            </div>
          </Alert>
        )}

        {hasAttemptedSubmit && errorMessages.length > 0 && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              fontFamily: 'inherit',
              '& .MuiAlert-message': {
                fontFamily: 'inherit',
              }
            }}
          >
            The address is still invalid. Please check the information below.
          </Alert>
        )}

        {(showStreetNumberField || showZipCodeField) && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: 'rgba(211, 47, 47, 0.1)',
              borderRadius: 1,
              border: '1px solid rgba(211, 47, 47, 0.3)'
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'inherit',
                fontWeight: 500,
                color: 'error.main',
                mb: 1
              }}
            >
              The following issues were found with your address:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {showStreetNumberField && (
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontFamily: 'inherit',
                    fontSize: '0.875rem'
                  }}
                >
                  Please enter the street number for your address.
                </Typography>
              )}
              {showZipCodeField && (
                <Typography
                  component="li"
                  variant="body2"
                  sx={{
                    fontFamily: 'inherit',
                    fontSize: '0.875rem'
                  }}
                >
                  Please enter the ZIP code for your address.
                </Typography>
              )}
            </ul>
          </Box>
        )}

        {showStreetNumberField && (
          <TextField
            label="Street Number"
            value={streetNumber}
            onChange={(e) => setStreetNumber(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g. 123"
            helperText={needsStreetNumber ? "Enter the street number for your address" : "Adding a street number may help with delivery"}
            required={needsStreetNumber}
            disabled={isValidating}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                fontFamily: '"Frank Ruhl Libre", serif',
                color: '#1D3557',
                '&.Mui-focused': {
                  color: '#1D3557',
                },
              },
              '& .MuiInputBase-root': {
                borderRadius: '8px',
                fontFamily: '"Frank Ruhl Libre", serif',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1D3557',
                  borderWidth: '1px',
                },
              },
              '& .MuiInputBase-input': {
                fontFamily: '"Frank Ruhl Libre", serif',
                padding: '14px 16px',
                fontSize: '1rem',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(29, 53, 87, 0.23)',
              },
              '& .MuiFormHelperText-root': {
                fontFamily: '"Frank Ruhl Libre", serif',
                fontSize: '0.75rem',
                marginTop: '6px',
              }
            }}
          />
        )}

        {showZipCodeField && (
          <TextField
            label="ZIP Code"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g. 12345"
            helperText="Enter the ZIP code for your address"
            required={needsZipCode}
            disabled={isValidating}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                fontFamily: '"Frank Ruhl Libre", serif',
                color: '#1D3557',
                '&.Mui-focused': {
                  color: '#1D3557',
                },
              },
              '& .MuiInputBase-root': {
                borderRadius: '8px',
                fontFamily: '"Frank Ruhl Libre", serif',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1D3557',
                  borderWidth: '1px',
                },
              },
              '& .MuiInputBase-input': {
                fontFamily: '"Frank Ruhl Libre", serif',
                padding: '14px 16px',
                fontSize: '1rem',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(29, 53, 87, 0.23)',
              },
              '& .MuiFormHelperText-root': {
                fontFamily: '"Frank Ruhl Libre", serif',
                fontSize: '0.75rem',
                marginTop: '6px',
              }
            }}
          />
        )}

        {isValidating && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            my: 3,
            py: 1.5,
            color: '#1D3557', // sitkablue color
            fontFamily: '"Frank Ruhl Libre", serif',
            backgroundColor: 'rgba(29, 53, 87, 0.05)', // light sitkablue background
            borderRadius: '8px',
          }}>
            <CircularProgress 
              size={20} 
              sx={{ 
                mr: 1.5,
                color: '#E76F51', // salmon color to match the button
              }} 
            />
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: '"Frank Ruhl Libre", serif',
                fontWeight: 500,
                fontSize: '0.95rem',
              }}
            >
              Validating Address...
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            onClick={onClose}
            sx={{
              fontFamily: '"Frank Ruhl Libre", serif',
              textTransform: 'none',
              fontWeight: 500,
              color: '#1D3557',
              backgroundColor: 'transparent',
              border: 'none',
              padding: '13px 0',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              }
            }}
            disabled={isValidating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disableElevation
            disabled={isValidating || (showStreetNumberField && needsStreetNumber && !streetNumber) ||
              (showZipCodeField && needsZipCode && !zipCode)}
            sx={{
              fontFamily: '"Frank Ruhl Libre", serif',
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: '48px',
              padding: '13px 24px',
              minWidth: '150px',
              backgroundColor: '#E76F51', // salmon color
              color: 'white',
              border: 'none',
              '&:hover': {
                backgroundColor: '#C65F45', // darker salmon on hover
              },
              '&:disabled': {
                backgroundColor: 'rgba(231, 111, 81, 0.6)', // lighter salmon when disabled
                color: 'white',
              }
            }}
          >
            {isValidating ? 'Validating...' : 'Save Address'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddressValidationModal;